import { eachDayOfInterval, format } from 'date-fns';
import { ActiveModifiers } from 'react-day-picker';
import {
  ProviderAvailabilityView,
  ProviderBookingNested,
  ShiftTypeEnum,
  StatusByProviderEnum,
  StatusByRepEnum,
} from '../../../../api';
import { DATE_FORMAT } from '../../../constants/time';

export const getBookingStatusLabelText = (booking: ProviderBookingNested): string => {
  const { status_by_provider, status_by_rep } = booking;
  switch (true) {
    case status_by_provider === StatusByProviderEnum.PENDING &&
      status_by_rep === StatusByRepEnum.PENDING:
      return 'Pending Provider’s Response';
    case status_by_rep === StatusByRepEnum.PENDING &&
      status_by_provider !== StatusByProviderEnum.PENDING:
      return 'Awaiting Facility Confirmation';
    case status_by_rep === StatusByRepEnum.ACCEPTED &&
      status_by_provider === StatusByProviderEnum.ACCEPTED:
      return 'Booked';
    default:
      return '';
  }
};

export const getBasicCellClassName = (activeModifiers: ActiveModifiers) => {
  if (activeModifiers.selected && activeModifiers.booked) {
    return 'booked-selected';
  }
  if (activeModifiers.booked) {
    return 'booked';
  }
  if (activeModifiers.selected) {
    return 'selected';
  }
  if (activeModifiers.available) {
    return 'available';
  }
  return 'default';
};

export const getContainerClassName = ({
  isLoading,
  isProvider,
  activeModifiers,
  schedule,
}: {
  activeModifiers: ActiveModifiers;
  isProvider: boolean;
  isLoading: boolean;
  schedule?: ProviderAvailabilityView | undefined;
}): string => {
  if (isLoading) {
    return 'unavailable';
  }
  const pendingStatus = schedule?.bookings
    ? getPendingClassName(schedule.bookings, isProvider)
    : null;
  return pendingStatus ? pendingStatus : getBasicCellClassName(activeModifiers);
};

export const getLabelClassName = (
  activeModifiers: ActiveModifiers,
  isLoading: boolean,
  isProvider: boolean
) => {
  if (isLoading) {
    return 'unavailable';
  }
  // here could be added some custom logic
  if (isProvider && activeModifiers.selected && !activeModifiers.schedule) {
    return 'available-selected';
  }
  return getBasicCellClassName(activeModifiers);
};

export const getDateClassName = (activeModifiers: ActiveModifiers, isLoading: boolean) => {
  if (isLoading) {
    return 'unavailable';
  }
  // here could be added some custom logic
  return getBasicCellClassName(activeModifiers);
};

export const getPendingClassName = (
  bookings: ProviderBookingNested[],
  isProvider: boolean
): string | null => {
  if (bookings?.length) {
    const { status_by_provider, status_by_rep } = bookings[0];
    switch (true) {
      case status_by_provider === StatusByProviderEnum.PENDING &&
        status_by_rep === StatusByRepEnum.PENDING:
        return `pending-status-${!isProvider ? 'lighten' : 'darken'}`;
      case status_by_provider !== StatusByProviderEnum.PENDING &&
        status_by_rep === StatusByRepEnum.PENDING:
        return `pending-status-${!isProvider ? 'darken' : 'lighten'}`;
      case status_by_provider !== StatusByProviderEnum.PENDING &&
        status_by_rep !== StatusByRepEnum.PENDING:
        return 'booked';
      default:
        return 'default';
    }
  }
  return null;
};

export const getBookingStatusLabel = (booking: ProviderBookingNested, isProvider: boolean) => {
  const { status_by_provider, status_by_rep } = booking;
  switch (true) {
    case status_by_provider === StatusByProviderEnum.PENDING &&
      status_by_rep === StatusByRepEnum.PENDING:
      return isProvider ? 'Respond' : 'Pending';
    case status_by_provider !== StatusByProviderEnum.PENDING ||
      status_by_rep !== StatusByRepEnum.PENDING:
      return 'Awaiting';
    default:
      return 'Daytime';
  }
};

export const getFormattedLabel = (
  shift_type: ShiftTypeEnum,
  schedule: ProviderAvailabilityView,
  isProvider: boolean
) => {
  if (schedule && schedule.bookings && schedule.bookings.length) {
    return getBookingStatusLabel(schedule.bookings[0], isProvider);
  }
  switch (true) {
    case shift_type === ShiftTypeEnum.OVERNIGHT:
      return 'Overnight';
    case shift_type === ShiftTypeEnum.DAYTIME:
      return 'Daytime';
    case shift_type === ShiftTypeEnum.ANYTIME:
      return 'Anytime';
    default:
      return 'Daytime';
  }
};

export function generateDateRange(date1: Date, date2: Date): Date[] {
  const startDate = date1 > date2 ? date2 : date1;
  const endDate = date1 > date2 ? date1 : date2;
  const dates = eachDayOfInterval({ start: startDate, end: endDate });
  dates.sort((a, b) => a.getTime() - b.getTime());
  return dates;
}

export const getSelectedRange = ({
  previousSelectedDate,
  shiftHeld,
  pendingDates,
  selectable,
  isBooking,
  date,
  availableDates,
}: {
  previousSelectedDate: Date | null;
  shiftHeld: boolean;
  pendingDates: Set<Date>;
  availableDates: Set<Date>;
  selectable: Set<string>;
  isBooking: boolean;
  date: Date;
}) => {
  if (previousSelectedDate && shiftHeld) {
    const range = generateDateRange(previousSelectedDate, date);
    const pending = new Set(Array.from(pendingDates).map((el) => format(el, DATE_FORMAT)));
    const available = new Set(Array.from(availableDates).map((el) => format(el, DATE_FORMAT)));
    return range.filter((el) => {
      return isBooking
        ? selectable.has(format(el, DATE_FORMAT))
        : available.has(format(el, DATE_FORMAT)) || !pending.has(format(el, DATE_FORMAT));
    });
  }

  return [date];
};
